import { useNavigate } from "react-router-dom";
import { Eye, ArrowRightLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAdminVenues } from "@/hooks/use-admin-venues";
import { useVenueSwitcher } from "@/contexts/venue-switcher-context";

const tierBadgeColor: Record<string, string> = {
  free: "bg-muted text-muted-foreground",
  basic: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  pro: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
};

const statusBadgeColor: Record<string, string> = {
  active: "bg-accent/20 text-accent",
  suspended: "bg-destructive/20 text-destructive",
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
};

const stripeBadgeColor: Record<string, string> = {
  active: "bg-accent/20 text-accent",
  past_due: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  canceled: "bg-destructive/20 text-destructive",
  none: "text-muted-foreground",
};

function getMRRFromTier(tier: string | null): string {
  if (!tier) return "—";
  const tierMrr: Record<string, number> = {
    free: 0,
    basic: 40,
    pro: 99,
  };
  const mrr = tierMrr[tier.toLowerCase()];
  return mrr !== undefined ? `$${mrr}/mo` : "—";
}

function SkeletonRow() {
  return (
    <tr className="border-b last:border-0 hover:bg-muted/50 transition-colors">
      <td className="px-4 py-4"><div className="h-4 bg-muted rounded w-32 animate-pulse" /></td>
      <td className="px-4 py-4"><div className="h-4 bg-muted rounded w-24 animate-pulse" /></td>
      <td className="px-4 py-4"><div className="h-6 bg-muted rounded-full w-16 animate-pulse" /></td>
      <td className="px-4 py-4"><div className="h-6 bg-muted rounded-full w-20 animate-pulse" /></td>
      <td className="px-4 py-4"><div className="h-4 bg-muted rounded w-12 animate-pulse" /></td>
      <td className="px-4 py-4"><div className="h-6 bg-muted rounded-full w-20 animate-pulse" /></td>
      <td className="px-4 py-4"><div className="h-4 bg-muted rounded w-16 animate-pulse" /></td>
      <td className="px-4 py-4"><div className="flex gap-2 justify-end"><div className="h-8 bg-muted rounded w-16 animate-pulse" /><div className="h-8 bg-muted rounded w-16 animate-pulse" /></div></td>
    </tr>
  );
}

export default function CustomerListPage() {
  const navigate = useNavigate();
  const { startImpersonating } = useVenueSwitcher();
  const { data: venues = [], isLoading, error } = useAdminVenues();

  if (isLoading) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Customers</h1>
          <p className="text-sm text-muted-foreground">All registered venues</p>
        </div>
        <div className="rounded-xl border bg-card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b text-left text-sm text-muted-foreground">
                <th className="px-4 py-3 font-medium">Venue Name</th>
                <th className="px-4 py-3 font-medium">Slug</th>
                <th className="px-4 py-3 font-medium">Tier</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Active Campaigns</th>
                <th className="px-4 py-3 font-medium">Stripe Status</th>
                <th className="px-4 py-3 font-medium">MRR</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(3)].map((_, idx) => (
                <SkeletonRow key={idx} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Customers</h1>
          <p className="text-sm text-muted-foreground">All registered venues</p>
        </div>
        <div className="flex h-64 items-center justify-center rounded-xl border bg-card">
          <p className="text-destructive">Failed to load venues. Please try again.</p>
        </div>
      </div>
    );
  }

  if (venues.length === 0) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Customers</h1>
          <p className="text-sm text-muted-foreground">All registered venues</p>
        </div>
        <div className="flex h-64 items-center justify-center rounded-xl border bg-card">
          <p className="text-muted-foreground">No venues registered yet</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Customers</h1>
        <p className="text-sm text-muted-foreground">All registered venues</p>
      </div>

      <div className="rounded-xl border bg-card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b text-left text-sm text-muted-foreground">
              <th className="px-4 py-3 font-medium">Venue Name</th>
              <th className="px-4 py-3 font-medium">Slug</th>
              <th className="px-4 py-3 font-medium">Tier</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Active Campaigns</th>
              <th className="px-4 py-3 font-medium">Stripe Status</th>
              <th className="px-4 py-3 font-medium">MRR</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {venues.map((venue) => (
              <tr
                key={venue.id}
                className="border-b last:border-0 hover:bg-muted/50 transition-colors"
              >
                <td className="px-4 py-4 font-medium">
                  <button
                    onClick={() => navigate(`/admin/venues/${venue.id}`)}
                    className="text-primary hover:underline"
                  >
                    {venue.name}
                  </button>
                </td>
                <td className="px-4 py-4 text-sm text-muted-foreground">
                  {venue.slug}
                </td>
                <td className="px-4 py-4">
                  <Badge
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      tierBadgeColor[venue.subscriptionTier?.toLowerCase() || "free"]
                    }`}
                  >
                    {venue.subscriptionTier || "free"}
                  </Badge>
                </td>
                <td className="px-4 py-4">
                  <Badge
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      statusBadgeColor[venue.status.toLowerCase()] ||
                      "bg-muted text-muted-foreground"
                    }`}
                  >
                    {venue.status.charAt(0).toUpperCase() + venue.status.slice(1)}
                  </Badge>
                </td>
                <td className="px-4 py-4 text-sm">
                  {venue.activeCampaignCount}
                </td>
                <td className="px-4 py-4">
                  <Badge
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      stripeBadgeColor[venue.subscription?.status || "none"]
                    }`}
                  >
                    {venue.subscription?.status || "—"}
                  </Badge>
                </td>
                <td className="px-4 py-4 text-sm">
                  {getMRRFromTier(venue.subscription?.tier || venue.subscriptionTier)}
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/admin/venues/${venue.id}`)}
                      title="View venue details"
                    >
                      <Eye className="h-4 w-4" /> View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        startImpersonating(venue.id, venue.name);
                        navigate("/app");
                      }}
                      className="text-amber-600 border-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-400 dark:hover:bg-amber-950"
                      title="Switch to this venue"
                    >
                      <ArrowRightLeft className="h-4 w-4" /> Switch
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
